<template>
   <div>
      <!-- Toggle & Tabs -->
      <div class="card mb-3">
         <div class="card-body m-2">
            <div class="row align-items-center">
               <div class="col-auto">
                  <ul class="nav nav-pills">
                     <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'block' }" href="#"
                           @click.prevent="switchTab('block')">
                           <i class="fa-solid fa-ban me-1"></i> Block List
                        </a>
                     </li>
                     <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'allow' }" href="#"
                           @click.prevent="switchTab('allow')">
                           <i class="fa-solid fa-check me-1"></i> Allow List
                        </a>
                     </li>
                     <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'logs' }" href="#"
                           @click.prevent="switchTab('logs')">
                           <i class="fa-solid fa-clock-rotate-left me-1"></i> DNS Logs
                        </a>
                     </li>
                     <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'setup' }" href="#"
                           @click.prevent="switchTab('setup')">
                           <i class="fa-solid fa-book me-1"></i> Setup Guide
                        </a>
                     </li>
                  </ul>
               </div>
            </div>
         </div>
      </div>

      <!-- Setup Guide Tab -->
      <div class="card mb-3" v-if="activeTab === 'setup'">
         <div class="card-body m-2">
            <h5 class="mb-3" style="color: #435666; font-weight: 700;">
               <i class="fa-solid fa-circle-info me-2" style="color: #00A0C1;"></i>
               How to Set Up DNS over HTTPS (DoH) Blocking
            </h5>
            <p class="text-muted mb-4" style="font-size: 14px;">
               DNS over HTTPS (DoH) encrypts DNS queries inside HTTPS requests, preventing traditional DNS-level filtering from inspecting them.
               By pointing your devices to XSurfLog's DoH resolver, all DNS queries are routed through your block/allow lists
               <strong>before</strong> the device connects to any website — providing network-level protection independent of the browser extension.
            </p>

            <div class="setup-section mb-4">
               <h6 class="setup-heading">
                  <i class="fa-solid fa-link me-2"></i> Your DoH Resolver URL
               </h6>
               <div class="setup-url-box">
                  <code class="setup-url-code">https://api.xsurflog.com/api/extension/v1/dns/<span style="color:#00A0C1;">{your_api_key}</span>/dns-query</code>
                  <small class="d-block text-muted mt-2">
                     <i class="fa-solid fa-key me-1"></i>
                     Replace <code>{your_api_key}</code> with your account API key from
                     <router-link to="/account-api-keys" style="color: #00A0C1;">Account API Keys</router-link>.
                  </small>
               </div>
            </div>

            <!-- OS Setup Accordion -->
            <div class="accordion" id="dohSetupAccordion">

               <!-- Windows -->
               <div class="accordion-item setup-accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed setup-accordion-btn" type="button"
                        data-bs-toggle="collapse" data-bs-target="#setupWindows">
                        <i class="fa-brands fa-windows me-2" style="color: #0078D4;"></i>
                        Windows 10 / 11
                     </button>
                  </h2>
                  <div id="setupWindows" class="accordion-collapse collapse" data-bs-parent="#dohSetupAccordion">
                     <div class="accordion-body setup-accordion-body">
                        <ol>
                           <li>Open <strong>Settings</strong> → <strong>Network & Internet</strong> → <strong>Wi-Fi</strong> (or <strong>Ethernet</strong>).</li>
                           <li>Click your active connection's <strong>Hardware properties</strong>.</li>
                           <li>Under <strong>DNS server assignment</strong>, click <strong>Edit</strong>.</li>
                           <li>Select <strong>Manual</strong>, enable <strong>IPv4</strong>.</li>
                           <li>Set <strong>Preferred DNS</strong> to your XSurfLog server IP.</li>
                           <li>Under <strong>DNS over HTTPS</strong>, select <strong>On (manual template)</strong>.</li>
                           <li>Enter your DoH URL: <code>https://api.xsurflog.com/api/extension/v1/dns/{your_api_key}/dns-query</code></li>
                           <li>Click <strong>Save</strong>.</li>
                        </ol>
                        <div class="alert alert-info py-2 mb-0" style="font-size: 13px;">
                           <i class="fa-solid fa-lightbulb me-1"></i>
                           <strong>Note:</strong> Windows 11 22H2+ has native DoH support. On older versions, use a third-party DoH client like <strong>dnscrypt-proxy</strong> or <strong>YogaDNS</strong>.
                        </div>
                     </div>
                  </div>
               </div>

               <!-- macOS -->
               <div class="accordion-item setup-accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed setup-accordion-btn" type="button"
                        data-bs-toggle="collapse" data-bs-target="#setupMac">
                        <i class="fa-brands fa-apple me-2" style="color: #333;"></i>
                        macOS
                     </button>
                  </h2>
                  <div id="setupMac" class="accordion-collapse collapse" data-bs-parent="#dohSetupAccordion">
                     <div class="accordion-body setup-accordion-body">
                        <p>macOS uses <strong>.mobileconfig</strong> profiles for DoH. Create a profile file:</p>
                        <pre class="setup-code-block">&lt;?xml version="1.0" encoding="UTF-8"?&gt;
&lt;!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN"
  "http://www.apple.com/DTDs/PropertyList-1.0.dtd"&gt;
&lt;plist version="1.0"&gt;
&lt;dict&gt;
  &lt;key&gt;PayloadContent&lt;/key&gt;
  &lt;array&gt;
    &lt;dict&gt;
      &lt;key&gt;DNSSettings&lt;/key&gt;
      &lt;dict&gt;
        &lt;key&gt;DNSProtocol&lt;/key&gt;
        &lt;string&gt;HTTPS&lt;/string&gt;
        &lt;key&gt;ServerURL&lt;/key&gt;
        &lt;string&gt;https://api.xsurflog.com/api/extension/v1/dns/{your_api_key}/dns-query&lt;/string&gt;
      &lt;/dict&gt;
      &lt;key&gt;PayloadType&lt;/key&gt;
      &lt;string&gt;com.apple.dnsSettings.managed&lt;/string&gt;
      &lt;key&gt;PayloadIdentifier&lt;/key&gt;
      &lt;string&gt;com.xsurflog.doh&lt;/string&gt;
      &lt;key&gt;PayloadUUID&lt;/key&gt;
      &lt;string&gt;A1B2C3D4-E5F6-7890-ABCD-EF1234567890&lt;/string&gt;
      &lt;key&gt;PayloadVersion&lt;/key&gt;
      &lt;integer&gt;1&lt;/integer&gt;
    &lt;/dict&gt;
  &lt;/array&gt;
  &lt;key&gt;PayloadDisplayName&lt;/key&gt;
  &lt;string&gt;XSurfLog DoH&lt;/string&gt;
  &lt;key&gt;PayloadIdentifier&lt;/key&gt;
  &lt;string&gt;com.xsurflog.doh.profile&lt;/string&gt;
  &lt;key&gt;PayloadType&lt;/key&gt;
  &lt;string&gt;Configuration&lt;/string&gt;
  &lt;key&gt;PayloadUUID&lt;/key&gt;
  &lt;string&gt;B2C3D4E5-F6A7-8901-BCDE-F12345678901&lt;/string&gt;
  &lt;key&gt;PayloadVersion&lt;/key&gt;
  &lt;integer&gt;1&lt;/integer&gt;
&lt;/dict&gt;
&lt;/plist&gt;</pre>
                        <ol>
                           <li>Save the file as <code>xsurflog-doh.mobileconfig</code>.</li>
                           <li>Double-click the file to open it in <strong>System Preferences</strong>.</li>
                           <li>Go to <strong>System Preferences</strong> → <strong>Profiles</strong> → <strong>Install</strong> the profile.</li>
                           <li>All DNS queries will now route through XSurfLog's DoH resolver.</li>
                        </ol>
                     </div>
                  </div>
               </div>

               <!-- Linux -->
               <div class="accordion-item setup-accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed setup-accordion-btn" type="button"
                        data-bs-toggle="collapse" data-bs-target="#setupLinux">
                        <i class="fa-brands fa-linux me-2" style="color: #FCC624;"></i>
                        Linux
                     </button>
                  </h2>
                  <div id="setupLinux" class="accordion-collapse collapse" data-bs-parent="#dohSetupAccordion">
                     <div class="accordion-body setup-accordion-body">
                        <p><strong>Option A: Using <code>systemd-resolved</code></strong> (Ubuntu 22.04+, Fedora 38+)</p>
                        <pre class="setup-code-block"># Edit /etc/systemd/resolved.conf
[Resolve]
DNS=YOUR_SERVER_IP
DNSOverTLS=yes

# Then restart
sudo systemctl restart systemd-resolved</pre>

                        <p class="mt-3"><strong>Option B: Using <code>dnscrypt-proxy</code></strong> (recommended)</p>
                        <pre class="setup-code-block"># Install
sudo apt install dnscrypt-proxy   # Debian/Ubuntu
sudo dnf install dnscrypt-proxy   # Fedora

# Edit /etc/dnscrypt-proxy/dnscrypt-proxy.toml
server_names = ['xsurflog']

[static.'xsurflog']
stamp = 'sdns://AgAAAAAAAAAAD...'
# Or use manual config:

[static.'xsurflog']
stamp = ''

[sources]
  # Leave empty; use manual server below

listen_addresses = ['127.0.0.1:53']

[forwarding_rules]
# Forward all queries to XSurfLog DoH

# Alternatively, set bootstrap resolver and use:
doh_servers = true
bootstrap_resolvers = ['1.1.1.1:53']

# Add under [static] section:
[static.'xsurflog']
stamp = 'sdns://...'
# Generate the stamp from your DoH URL at:
# https://dnscrypt.info/stamps/

# Then restart
sudo systemctl restart dnscrypt-proxy</pre>

                        <div class="alert alert-info py-2 mb-0" style="font-size: 13px;">
                           <i class="fa-solid fa-lightbulb me-1"></i>
                           <strong>Tip:</strong> The XSurfLog Linux Agent can automate this configuration. Check the
                           <router-link to="/platform-guide/installation" style="color: #00A0C1;">Installation Guide</router-link> for agent setup.
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Android -->
               <div class="accordion-item setup-accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed setup-accordion-btn" type="button"
                        data-bs-toggle="collapse" data-bs-target="#setupAndroid">
                        <i class="fa-brands fa-android me-2" style="color: #3DDC84;"></i>
                        Android 9+
                     </button>
                  </h2>
                  <div id="setupAndroid" class="accordion-collapse collapse" data-bs-parent="#dohSetupAccordion">
                     <div class="accordion-body setup-accordion-body">
                        <ol>
                           <li>Open <strong>Settings</strong> → <strong>Network & Internet</strong> → <strong>Private DNS</strong>.</li>
                           <li>Select <strong>Private DNS provider hostname</strong>.</li>
                           <li>Enter your XSurfLog DoH hostname: <code>api.xsurflog.com</code></li>
                           <li>Tap <strong>Save</strong>.</li>
                        </ol>
                        <div class="alert alert-warning py-2 mb-0" style="font-size: 13px;">
                           <i class="fa-solid fa-triangle-exclamation me-1"></i>
                           <strong>Note:</strong> Android's Private DNS uses DNS over TLS (DoT) by default. For full DoH support with API key authentication, consider using the <strong>Intra</strong> app from the Play Store and configuring it with your full DoH URL.
                        </div>
                     </div>
                  </div>
               </div>

               <!-- iOS -->
               <div class="accordion-item setup-accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed setup-accordion-btn" type="button"
                        data-bs-toggle="collapse" data-bs-target="#setupIOS">
                        <i class="fa-brands fa-apple me-2" style="color: #333;"></i>
                        iOS / iPadOS
                     </button>
                  </h2>
                  <div id="setupIOS" class="accordion-collapse collapse" data-bs-parent="#dohSetupAccordion">
                     <div class="accordion-body setup-accordion-body">
                        <ol>
                           <li>Use the same <code>.mobileconfig</code> profile as macOS (see above).</li>
                           <li>Email the profile to the device or host it on a web server.</li>
                           <li>Open the file on the iOS device → <strong>Settings</strong> will prompt to install the profile.</li>
                           <li>Go to <strong>Settings</strong> → <strong>General</strong> → <strong>VPN & Device Management</strong> → Install the profile.</li>
                        </ol>
                     </div>
                  </div>
               </div>

               <!-- Browser-level -->
               <div class="accordion-item setup-accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed setup-accordion-btn" type="button"
                        data-bs-toggle="collapse" data-bs-target="#setupBrowser">
                        <i class="fa-solid fa-globe me-2" style="color: #00A0C1;"></i>
                        Browser-Level (Chrome / Firefox / Edge)
                     </button>
                  </h2>
                  <div id="setupBrowser" class="accordion-collapse collapse" data-bs-parent="#dohSetupAccordion">
                     <div class="accordion-body setup-accordion-body">
                        <p><strong>Google Chrome / Microsoft Edge:</strong></p>
                        <ol>
                           <li>Go to <code>chrome://settings/security</code> (or <code>edge://settings/privacy</code>).</li>
                           <li>Scroll to <strong>Use secure DNS</strong>.</li>
                           <li>Select <strong>With: Custom</strong>.</li>
                           <li>Enter: <code>https://api.xsurflog.com/api/extension/v1/dns/{your_api_key}/dns-query</code></li>
                        </ol>
                        <p class="mt-3"><strong>Mozilla Firefox:</strong></p>
                        <ol>
                           <li>Go to <code>about:preferences</code> → scroll to <strong>Network Settings</strong> → <strong>Settings</strong>.</li>
                           <li>Check <strong>Enable DNS over HTTPS</strong>.</li>
                           <li>Select <strong>Custom</strong> provider.</li>
                           <li>Enter: <code>https://api.xsurflog.com/api/extension/v1/dns/{your_api_key}/dns-query</code></li>
                        </ol>
                     </div>
                  </div>
               </div>

            </div>

            <!-- How it works -->
            <div class="mt-4 p-3" style="background: #f8f9fa; border-radius: 8px; border-left: 4px solid #00A0C1;">
               <h6 style="color: #435666; font-weight: 700;">
                  <i class="fa-solid fa-shield-halved me-2" style="color: #00A0C1;"></i>
                  How It Works
               </h6>
               <ul class="mb-0" style="font-size: 13px; color: #5a6d7e;">
                  <li>When a device uses XSurfLog as its DoH resolver, every DNS query is sent encrypted to this server.</li>
                  <li>The server checks the queried domain against your <strong>Allow List</strong> first (allow always wins).</li>
                  <li>If the domain is in the <strong>Block List</strong>, the server returns <code>0.0.0.0</code> — the device cannot connect.</li>
                  <li>If the domain is not in either list, the query is forwarded to an upstream resolver (e.g. Cloudflare) and the real answer is returned.</li>
                  <li>All queries are logged in the <strong>DNS Logs</strong> tab for visibility and auditing.</li>
               </ul>
            </div>

         </div>
      </div>

      <!-- Block List Tab -->
      <div class="card" v-if="activeTab === 'block'">
         <div class="card-body m-2">
            <div class="row p-2 align-items-center">
               <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
                  <input v-model.trim="blockInput" type="text" class="form-control form-control-sm"
                     placeholder="&#xF002; Search or add blocked domains / IPs..."
                     style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                     @keyup="onSearchBlock()" v-on:keyup.enter="addBlockEntry" />
               </div>
               <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
                  <button type="button" class="fw-bold btn btn-danger text-light btn-sm"
                     style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap;"
                     @click="addBlockEntry">
                     <i class="fa-solid fa-ban me-1"></i> Block Domain / IP
                  </button>
               </div>
               <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Sort by:</span>
                     <select v-model="blockSortBy" class="form-select shadow-none filterOptions bordered-select">
                        <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                     </select>
                  </div>
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Rows:</span>
                     <select v-model="blockPagination.pageSize"
                        class="form-select form-select-sm shadow-none filterOptions bordered-select"
                        style="width: auto;">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                        </option>
                     </select>
                  </div>
                  <div class="pagination" v-if="blockPagination.totalItems > 0">
                     <button @click="blockPagination.page > 1 ? blockPagination.page -= 1 : ''"
                        :disabled="blockPagination.page == 1" class="pagination-arrow">
                        <i class="fa fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ blockPaginationLabel }}</strong> of {{ blockPagination.totalItems }}
                     </span>
                     <button @click="blockPagination.page < blockPagination.totalPages ? blockPagination.page += 1 : ''"
                        :disabled="blockPagination.page >= blockPagination.totalPages" class="pagination-arrow">
                        <i class="fa fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <div class="mt-2 ms-2">
               <small class="text-muted">
                  <i class="fa-solid fa-circle-info me-1" style="color: #00A0C1;"></i>
                  Use the <strong>domainname.com</strong> format when adding websites. Wildcards (<strong>*.example.com</strong>, <strong>example.com/path/*</strong>), CIDR notation (<strong>192.168.1.0/24</strong>), IPs, and localhost are also supported. Ports are ignored by default. <strong>Note: localhost is always allowed.</strong>
               </small>
            </div>

            <div class="row p-2">
               <table class="table align-middle mainTable">
                  <thead class="align-middle">
                     <tr>
                        <th scope="col">Domain / IP</th>
                        <th scope="col">Resolved IPs</th>
                        <th scope="col" class="text-center">Actions</th>
                     </tr>
                  </thead>
                  <tbody v-if="blockPagination.totalItems <= 0 && !isLoading">
                     <tr>
                        <td colspan="3">No records to display.</td>
                     </tr>
                  </tbody>
                  <tbody v-else>
                     <tr v-for="(entry, index) in blockEntries" :key="index">
                        <td>
                           <i class="fa-solid fa-ban me-2" style="color: #dc3545;"></i>
                           {{ entry.domain }}
                        </td>
                        <td>
                           <small class="text-muted">{{ entry.resolved_ips || '—' }}</small>
                        </td>
                        <td class="text-center">
                           <button type="button" class="ms-action-btn ms-action-btn--delete"
                               @click="deleteBlockEntry(index)"
                              title="Remove from DoH block list">
                              <i class="fa fa-xmark"></i>
                           </button>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>
         </div>
      </div>

      <!-- Allow List Tab -->
      <div class="card" v-if="activeTab === 'allow'">
         <div class="card-body m-2">
            <div class="row p-2 align-items-center">
               <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
                  <input v-model.trim="allowInput" type="text" class="form-control form-control-sm"
                     placeholder="&#xF002; Search or add allowed domains / IPs..."
                     style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                     @keyup="onSearchAllow()" v-on:keyup.enter="addAllowEntry" />
               </div>
               <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
                  <button type="button" class="fw-bold btn btn-success text-light btn-sm"
                     style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap;"
                     @click="addAllowEntry">
                     <i class="fa-solid fa-check me-1"></i> Allow Domain / IP
                  </button>
               </div>
               <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Sort by:</span>
                     <select v-model="allowSortBy" class="form-select shadow-none filterOptions bordered-select">
                        <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                     </select>
                  </div>
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Rows:</span>
                     <select v-model="allowPagination.pageSize"
                        class="form-select form-select-sm shadow-none filterOptions bordered-select"
                        style="width: auto;">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                        </option>
                     </select>
                  </div>
                  <div class="pagination" v-if="allowPagination.totalItems > 0">
                     <button @click="allowPagination.page > 1 ? allowPagination.page -= 1 : ''"
                        :disabled="allowPagination.page == 1" class="pagination-arrow">
                        <i class="fa fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ allowPaginationLabel }}</strong> of {{ allowPagination.totalItems }}
                     </span>
                     <button
                        @click="allowPagination.page < allowPagination.totalPages ? allowPagination.page += 1 : ''"
                        :disabled="allowPagination.page >= allowPagination.totalPages" class="pagination-arrow">
                        <i class="fa fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <div class="mt-2 ms-2">
               <small class="text-muted">
                  <i class="fa-solid fa-circle-info me-1" style="color: #00A0C1;"></i>
                  Use the <strong>domainname.com</strong> format when adding websites. Wildcards (<strong>*.example.com</strong>, <strong>example.com/path/*</strong>), CIDR notation (<strong>192.168.1.0/24</strong>), IPs, and localhost are also supported. Ports are ignored by default. <strong>Note: localhost is always allowed.</strong>
               </small>
            </div>

            <div class="row p-2">
               <table class="table align-middle mainTable">
                  <thead class="align-middle">
                     <tr>
                        <th scope="col">Domain / IP</th>
                        <th scope="col">Resolved IPs</th>
                        <th scope="col" class="text-center">Actions</th>
                     </tr>
                  </thead>
                  <tbody v-if="allowPagination.totalItems <= 0 && !isLoading">
                     <tr>
                        <td colspan="3">No records to display.</td>
                     </tr>
                  </tbody>
                  <tbody v-else>
                     <tr v-for="(entry, index) in allowEntries" :key="index">
                        <td>
                           <i class="fa-solid fa-check me-2" style="color: #198754;"></i>
                           {{ entry.domain }}
                        </td>
                        <td>
                           <small class="text-muted">{{ entry.resolved_ips || '—' }}</small>
                        </td>
                        <td class="text-center">
                           <button type="button" class="ms-action-btn ms-action-btn--delete"
                               @click="deleteAllowEntry(index)"
                              title="Remove from DoH allow list">
                              <i class="fa fa-xmark"></i>
                           </button>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>
         </div>
      </div>

      <!-- DNS Logs Tab -->
      <div class="card" v-if="activeTab === 'logs'">
         <div class="card-body m-2">
            <div class="row p-2 align-items-center">
               <div class="col-12 col-lg-4 mb-2 mb-lg-0">
                  <input v-model.trim="logSearch" type="text" class="form-control form-control-sm"
                     placeholder="&#xF002; Search DNS logs..."
                     style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                     @keyup="onSearchLogs()" />
               </div>
               <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Sort by:</span>
                     <select v-model="logSortBy" class="form-select shadow-none filterOptions bordered-select">
                        <option value="date_desc">Date: Newest</option>
                        <option value="date_asc">Date: Oldest</option>
                        <option value="domain_asc">Domain: A-Z</option>
                        <option value="blocked_first">Blocked First</option>
                        <option value="allowed_first">Allowed First</option>
                     </select>
                  </div>
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Rows:</span>
                     <select v-model="logPagination.pageSize"
                        class="form-select form-select-sm shadow-none filterOptions bordered-select"
                        style="width: auto;">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                        </option>
                     </select>
                  </div>
                  <div class="pagination" v-if="logPagination.totalItems > 0">
                     <button @click="logPagination.page > 1 ? logPagination.page -= 1 : ''"
                        :disabled="logPagination.page == 1" class="pagination-arrow">
                        <i class="fa fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ logPaginationLabel }}</strong> of {{ logPagination.totalItems }}
                     </span>
                     <button @click="logPagination.page < logPagination.totalPages ? logPagination.page += 1 : ''"
                        :disabled="logPagination.page >= logPagination.totalPages" class="pagination-arrow">
                        <i class="fa fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <div class="row p-2">
               <table class="table align-middle mainTable">
                  <thead class="align-middle">
                     <tr>
                        <th scope="col">Domain</th>
                        <th scope="col">Type</th>
                        <th scope="col">Response</th>
                        <th scope="col">Status</th>
                        <th scope="col">Source IP</th>
                        <th scope="col">Date</th>
                     </tr>
                  </thead>
                  <tbody v-if="logPagination.totalItems <= 0 && !isLoading">
                     <tr>
                        <td colspan="6">No DNS logs to display.</td>
                     </tr>
                  </tbody>
                  <tbody v-else>
                     <tr v-for="(log, index) in logEntries" :key="index">
                        <td>{{ log.queried_domain }}</td>
                        <td><span class="badge bg-secondary">{{ log.query_type || 'A' }}</span></td>
                        <td><small class="text-muted">{{ log.response_ip || '—' }}</small></td>
                        <td>
                           <span v-if="log.was_blocked" class="badge bg-danger">Blocked</span>
                           <span v-else class="badge bg-success">Allowed</span>
                        </td>
                        <td><small class="text-muted">{{ log.source_ip || '—' }}</small></td>
                        <td><small>{{ formatDate(log.date_created) }}</small></td>
                     </tr>
                  </tbody>
               </table>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce, IsValidIPV4, ExtractDomain, IsValidWildcard, IsValidCIDR, IsUniversalWildcard, extractErrorMessage, IsLocalhost } from '@/plugins/helper.js'

export default {
   data() {
      return {
         activeTab: 'block',
         isLoading: false,

         // Block list
         blockEntries: [],
         blockInput: "",
         blockSortBy: "domain__asc",
         blockPagination: { pageSize: 50, page: 1, totalItems: 0, totalPages: 1 },

         // Allow list
         allowEntries: [],
         allowInput: "",
         allowSortBy: "domain__asc",
         allowPagination: { pageSize: 50, page: 1, totalItems: 0, totalPages: 1 },

         // Logs
         logEntries: [],
         logSearch: "",
         logSortBy: "date_desc",
         logPagination: { pageSize: 50, page: 1, totalItems: 0, totalPages: 1 },

         // Shared
         debounceFnBlock: null,
         debounceFnAllow: null,
         debounceFnLogs: null,
         sortByList: [
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
            { text: "Domain: A-Z", value: "domain__asc" },
            { text: "Domain: Z-A", value: "domain__desc" },
         ],
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
      }
   },

   computed: {
      blockPaginationLabel() {
         let start = Math.min((this.blockPagination.page - 1) * this.blockPagination.pageSize + 1, this.blockPagination.totalItems)
         let end = Math.min(this.blockPagination.page * this.blockPagination.pageSize, this.blockPagination.totalItems)
         return start + " - " + end
      },
      allowPaginationLabel() {
         let start = Math.min((this.allowPagination.page - 1) * this.allowPagination.pageSize + 1, this.allowPagination.totalItems)
         let end = Math.min(this.allowPagination.page * this.allowPagination.pageSize, this.allowPagination.totalItems)
         return start + " - " + end
      },
      logPaginationLabel() {
         let start = Math.min((this.logPagination.page - 1) * this.logPagination.pageSize + 1, this.logPagination.totalItems)
         let end = Math.min(this.logPagination.page * this.logPagination.pageSize, this.logPagination.totalItems)
         return start + " - " + end
      },
   },

   watch: {
      'blockPagination.page'() { this.getBlockList() },
      'blockPagination.pageSize'() { this.blockPagination.page = 1; this.getBlockList() },
      blockSortBy() { this.getBlockList() },
      'allowPagination.page'() { this.getAllowList() },
      'allowPagination.pageSize'() { this.allowPagination.page = 1; this.getAllowList() },
      allowSortBy() { this.getAllowList() },
      'logPagination.page'() { this.getLogs() },
      'logPagination.pageSize'() { this.logPagination.page = 1; this.getLogs() },
      logSortBy() { this.getLogs() },
   },

   created() {
      this.debounceFnBlock = debounce(() => this.getBlockList(), 500)
      this.debounceFnAllow = debounce(() => this.getAllowList(), 500)
      this.debounceFnLogs = debounce(() => this.getLogs(), 500)
   },

   mounted() {
      this.getBlockList()
   },

   methods: {
      switchTab(tab) {
         this.activeTab = tab
         if (tab === 'block') this.getBlockList()
         else if (tab === 'allow') this.getAllowList()
         else if (tab === 'logs') this.getLogs()
      },

      onSearchBlock() { this.debounceFnBlock() },
      onSearchAllow() { this.debounceFnAllow() },
      onSearchLogs() { this.debounceFnLogs() },

      parseSortBy(sortByValue) {
         if (sortByValue.includes("__")) {
            let parts = sortByValue.split("__")
            return { field: parts[0], order: parts[1] }
         }
         return { field: sortByValue, order: "" }
      },

      buildApiPath(base, pagination, sortByValue, searchKey, searchParam) {
         let path = base + "?page_size=" + pagination.pageSize + "&page=" + pagination.page
         let sort = this.parseSortBy(sortByValue)
         if (sort.field && sort.order) {
            path += "&sort_by=" + sort.field + "&order_by=" + sort.order
         }
         if (searchKey) {
            path += "&" + searchParam + "=" + searchKey
         }
         return path
      },

      validateAndExtractDomain(input) {
         if (!input || input.trim() === "") return null
         if (IsUniversalWildcard(input)) return null

         let domain = input
         if (domain[domain.length - 1] === '/' && !domain.includes('*')) {
            domain = domain.substring(0, domain.length - 1)
         }

         if (IsValidWildcard(domain)) return domain
         if (IsValidCIDR(domain)) return domain
         if (IsValidIPV4(domain)) return domain

         let extracted = ExtractDomain(domain)
         if (extracted && extracted.includes("www.")) extracted = extracted.replace("www.", "")

         const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
         if (extracted && domainPattern.test(extracted)) return extracted

         return null
      },

      // ── Block List ──
      async getBlockList() {
         let path = this.buildApiPath(
            "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-doh-block-list",
            this.blockPagination, this.blockSortBy, this.blockInput, "search_key"
         )
         this.isLoading = true
         try {
            const resp = await this.$axios.get(path)
            this.blockEntries = resp.data.doh_block_list === "No Record Found" ? [] : resp.data.doh_block_list
            if (resp.data.pagination) {
               this.blockPagination.totalItems = resp.data.pagination.total_count
               this.blockPagination.totalPages = Math.ceil(resp.data.pagination.total_count / this.blockPagination.pageSize)
            }
         } catch (error) {
            console.log(error)
         } finally {
            this.isLoading = false
         }
      },

      async addBlockEntry() {
         if (IsLocalhost(this.blockInput)) {
            this.$toast.error("Localhost is permanently permitted. It cannot be added to allow list or block list.")
            return
         }
         let domain = this.validateAndExtractDomain(this.blockInput)
         if (!domain) {
            this.$toast.error("Please provide a valid domain, IP, wildcard, or CIDR.")
            return
         }
         try {
            const resp = await this.$axios.post(
               "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/add-doh-block-entry",
               { domain: domain.toLowerCase() }
            )
            if (resp.data.success != null && resp.data.success === false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
            }
            this.blockInput = ""
            this.getBlockList()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to add domain to DoH block list."))
            this.blockInput = ""
            this.getBlockList()
         }
      },

      async deleteBlockEntry(index) {
         try {
            const entry = this.blockEntries[index]
            const resp = await this.$axios.delete(
               "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/remove-doh-block-entry/" + entry.id
            )
            if (resp.data.success != null && resp.data.success === false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getBlockList()
            }
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to remove domain from DoH block list."))
         }
      },

      // ── Allow List ──
      async getAllowList() {
         let path = this.buildApiPath(
            "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-doh-allow-list",
            this.allowPagination, this.allowSortBy, this.allowInput, "search_key"
         )
         this.isLoading = true
         try {
            const resp = await this.$axios.get(path)
            this.allowEntries = resp.data.doh_allow_list === "No Record Found" ? [] : resp.data.doh_allow_list
            if (resp.data.pagination) {
               this.allowPagination.totalItems = resp.data.pagination.total_count
               this.allowPagination.totalPages = Math.ceil(resp.data.pagination.total_count / this.allowPagination.pageSize)
            }
         } catch (error) {
            console.log(error)
         } finally {
            this.isLoading = false
         }
      },

      async addAllowEntry() {
         if (IsLocalhost(this.allowInput)) {
            this.$toast.error("Localhost is permanently permitted. It cannot be added to allow list or block list.")
            return
         }
         let domain = this.validateAndExtractDomain(this.allowInput)
         if (!domain) {
            this.$toast.error("Please provide a valid domain, IP, wildcard, or CIDR.")
            return
         }
         try {
            const resp = await this.$axios.post(
               "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/add-doh-allow-entry",
               { domain: domain.toLowerCase() }
            )
            if (resp.data.success != null && resp.data.success === false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
            }
            this.allowInput = ""
            this.getAllowList()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to add domain to DoH allow list."))
            this.allowInput = ""
            this.getAllowList()
         }
      },

      async deleteAllowEntry(index) {
         try {
            const entry = this.allowEntries[index]
            const resp = await this.$axios.delete(
               "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/remove-doh-allow-entry/" + entry.id
            )
            if (resp.data.success != null && resp.data.success === false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getAllowList()
            }
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to remove domain from DoH allow list."))
         }
      },

      // ── DNS Logs ──
      async getLogs() {
         let path = "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-doh-logs"
            + "?page_size=" + this.logPagination.pageSize
            + "&page=" + this.logPagination.page
            + "&sort_by=" + this.logSortBy
         if (this.logSearch) {
            path += "&search=" + this.logSearch
         }
         this.isLoading = true
         try {
            const resp = await this.$axios.get(path)
            this.logEntries = resp.data.doh_logs || []
            if (resp.data.pagination) {
               this.logPagination.totalItems = resp.data.pagination.total_records
               this.logPagination.totalPages = resp.data.pagination.total_pages
            }
         } catch (error) {
            console.log(error)
         } finally {
            this.isLoading = false
         }
      },

      formatDate(dateStr) {
         if (!dateStr) return '—'
         const d = new Date(dateStr)
         return d.toLocaleString()
      },
   }
}
</script>

<style scoped>
.nav-pills .nav-link {
   color: #435666;
   font-weight: 600;
   font-size: 14px;
   border-radius: 8px;
}

.nav-pills .nav-link.active {
   background-color: #00A0C1;
   color: #fff;
}

.nav-pills .nav-link:hover:not(.active) {
   background-color: #e9ecef;
}

/* Setup Guide Styles */
.setup-heading {
   color: #435666;
   font-weight: 700;
   font-size: 15px;
   margin-bottom: 12px;
}

.setup-url-box {
   background: #f0f7fa;
   border: 1px solid #d0e8f0;
   border-radius: 8px;
   padding: 16px;
}

.setup-url-code {
   font-size: 14px;
   color: #333;
   word-break: break-all;
}

.setup-accordion-item {
   border: 1px solid #e9ecef;
   border-radius: 8px !important;
   margin-bottom: 8px;
   overflow: hidden;
}

.setup-accordion-btn {
   font-weight: 600;
   font-size: 14px;
   color: #435666;
   background: #fff;
}

.setup-accordion-btn:not(.collapsed) {
   background: #f0f7fa;
   color: #00A0C1;
   box-shadow: none;
}

.setup-accordion-body {
   font-size: 13px;
   color: #5a6d7e;
   line-height: 1.7;
}

.setup-accordion-body ol {
   padding-left: 20px;
}

.setup-accordion-body ol li {
   margin-bottom: 6px;
}

.setup-code-block {
   background: #1e2a3a;
   color: #e0e6ed;
   border-radius: 8px;
   padding: 16px;
   font-size: 12px;
   overflow-x: auto;
   white-space: pre;
   line-height: 1.5;
}
</style>
